import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Box,
} from "@mui/material";
import { useState } from "react";
import { useCreateWorkCommentMutation } from "../../store/services/timelineApiRTK";

interface AddCommentDialogProps {
  open: boolean;
  onClose: () => void;
  workId: number;
  workName: string;
}

const AddCommentDialog = ({
  open,
  onClose,
  workId,
  workName,
}: AddCommentDialogProps) => {
  const [comment, setComment] = useState("");
  const [createComment, { isLoading, error }] = useCreateWorkCommentMutation();

  const handleSubmit = async () => {
    if (!comment.trim()) return;

    try {
      await createComment({
        CustomerId: null,
        Description: comment.trim(),
        Name: null,
        WorkId: workId,
      }).unwrap();

      // Success - close dialog and reset form
      setComment("");
      onClose();
    } catch (err) {
      // Error handled by RTK Query error state
      console.error("Failed to create comment:", err);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setComment("");
      onClose();
    }
  };

  const isSubmitDisabled = !comment.trim() || isLoading;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
          },
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" fontWeight="600">
          Lägg till kommentar
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Ärende: {workName}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Kunde inte spara kommentaren. Försök igen senare.
            </Typography>
          </Alert>
        )}

        <TextField
          autoFocus
          fullWidth
          multiline
          rows={10}
          variant="outlined"
          label="Kommentar"
          placeholder="Skriv din kommentar här..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          disabled={isLoading}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
          helperText={`${comment.length} tecken`}
        />
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={isLoading}
          sx={{ borderRadius: 2 }}
        >
          Avbryt
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitDisabled}
          sx={{ borderRadius: 2, position: "relative" }}
        >
          {isLoading && (
            <Box
              sx={{
                position: "absolute",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <CircularProgress size={20} color="inherit" />
            </Box>
          )}
          <span style={{ opacity: isLoading ? 0 : 1 }}>
            Spara kommentar
          </span>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCommentDialog;